import { useState, useEffect, useRef } from 'preact/hooks'
import { Save, X, Calendar, Globe, AlertTriangle } from 'lucide-preact'
import { websApi, type WebViewDto, type OnlineEventDetailsPatchDto } from '@/api/webs'
import { touchPointsApi } from '@/api/touchPoints'
import { integrationsApi } from '@/api/integrations'
import type { BigMarkerChannelDto } from '@/types/integrations'
import { parseCascadeError, type CascadeError } from '@/utils/cascadeError'
import { WebTypeCascadeModal } from '@/components/webs/modals/WebTypeCascadeModal'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { TextBox, TextBoxField } from '@/components'
import { Select, type SelectOption } from '@/components/common/Select'
import { DateTimePicker } from '@/components/common/DateTimePicker'
import { TimeZoneCombobox } from '@/components/common/TimeZoneCombobox'
import { useLookups } from '@/hooks/useLookups'
import type { OptionDto } from '@/types/lookups'
import { DetailRow } from './DetailRow'
import { EditButton } from './EditButton'
import { sanitizeDateTimeInput, formatEventDateTime } from '@/utils/dateTimeFormatters'
import { BRAND_CLASSES } from '@/constants/theme'
import { INTEGRATION_STATUS_COLORS } from '@/types/integrations'
import { ToggleRow } from '@/components/common/ToggleRow'
import type { Control, UseFormRegister, UseFormWatch, UseFormSetValue, UseFormGetFieldState, FormState } from 'react-hook-form'
import type { OnlineEventAIForm } from '@/hooks/useOnlineEventForm'
import { isOnDemandMode } from '@/utils/onlineEventMode'

const NO_CHANNEL_LABEL = 'None — no BigMarker webinar'

interface OnlineEventDetailsCardProps {
  evt: WebViewDto['OnlineEvent']
  web: WebViewDto
  editing: boolean
  saving: boolean
  editDisabled?: boolean
  integrationError?: boolean
  integrationName: string
  onEdit: () => void
  onCancel: () => void
  onSaved: () => void
  onSaving: (v: boolean) => void
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
  control: Control<OnlineEventAIForm>
  register: UseFormRegister<OnlineEventAIForm>
  watch: UseFormWatch<OnlineEventAIForm>
  setValue: UseFormSetValue<OnlineEventAIForm>
  getFieldState: UseFormGetFieldState<OnlineEventAIForm>
  formState: FormState<OnlineEventAIForm>
}

export function OnlineEventDetailsCard({
  evt, web, editing, saving, editDisabled, integrationError, integrationName,
  onEdit, onCancel, onSaved, onSaving, onPatchWeb,
  control,
  register: formRegister, watch: formWatch, setValue: formSetValue,
  getFieldState: formGetFieldState, formState: currentFormState,
}: OnlineEventDetailsCardProps) {
  const { showError, showSuccess } = useToast()
  const { canEdit } = useWorkspaceCapabilities()
  const [cascadeError, setCascadeError] = useState<CascadeError | null>(null)
  const [pendingPatchDto, setPendingPatchDto] = useState<OnlineEventDetailsPatchDto | null>(null)
  const { getOnlineEventTypes, getOnlineModeTypes, getTimeZones } = useLookups()
  const eventTypeOptions: OptionDto[] = getOnlineEventTypes() || []
  const modeTypeOptions: OptionDto[] = getOnlineModeTypes() || []
  const timeZones = getTimeZones() || []

  const resolveDescription = (val?: string, options: OptionDto[] = []) => {
    const trimmed = (val || '').trim()
    if (!trimmed) return '\u2014'
    return options.find(o => o.Key === trimmed)?.Description
      ?? options.find(o => o.Description === trimmed)?.Description
      ?? trimmed
  }

  const resolveOptionKey = (val?: string, options: OptionDto[] = []): string => {
    const trimmed = (val || '').trim()
    if (!trimmed) return ''
    return options.find(o => o.Key === trimmed)?.Key
      ?? options.find(o => o.Description === trimmed)?.Key
      ?? trimmed
  }

  const watched = formWatch()

  // The channel is only ever chosen for a conference OrbitalX still has to create. An imported one
  // already carries BigMarker's own channel, and channel_id cannot be changed after create anyway.
  const conferenceExists = (evt?.ConferenceId || '').trim().length > 0
    || (watched?.conferenceId || '').trim().length > 0
  const [channels, setChannels] = useState<BigMarkerChannelDto[]>([])
  // The default seeded below, so Cancel can put the form back to what it opened on.
  const seededChannelRef = useRef('')
  // Keyed on the workspace alone. conferenceExists now moves as the user types a Conference Id,
  // and this fetch must not chase that — the default is only ever seeded once, guarded below.
  useEffect(() => {
    let cancelled = false
    integrationsApi.getBigMarkerChannels(web.WorkspaceId).then(res => {
      if (cancelled || res.error || !res.data) return
      setChannels(res.data)
      // Open on where this company published last, but never overwrite a choice already made,
      // and never seed one onto an event that is importing an existing conference, or onto an
      // On-demand recording, where BigMarker is opt-in (ORB-1076). The mode is read from the form
      // as it is now, since the user can change it before this answers.
      const preset = (formWatch('channelId') || '').trim()
      const importing = ((evt?.ConferenceId || '') + (formWatch('conferenceId') || '')).trim().length > 0
      const fallback = res.data.find(c => c.IsDefault)?.ChannelId
      if (!preset && !importing && !isOnDemandMode(formWatch('modeType')) && fallback) {
        seededChannelRef.current = fallback
        formSetValue('channelId', fallback)
      }
    })
    return () => { cancelled = true }
  }, [web.WorkspaceId])

  const initial = {
    eventType: resolveOptionKey(evt?.EventType, eventTypeOptions),
    modeType: resolveOptionKey(evt?.ModeType, modeTypeOptions),
    title: evt?.Title || '', timeZone: evt?.TimeZone || '',
    startDateTime: sanitizeDateTimeInput(evt?.StartDateTime),
    endDateTime: sanitizeDateTimeInput(evt?.EndDateTime),
    conferenceId: evt?.ConferenceId || '',
    channelId: evt?.ChannelId || '',
    holdCrmSyncUntilEnd: evt?.HoldCrmSyncUntilEnd ?? false,
  }
  const current = {
    eventType: (watched?.eventType || '').trim(), modeType: (watched?.modeType || '').trim(),
    title: (watched?.title || '').trim(), timeZone: (watched?.timeZone || '').trim(),
    startDateTime: (watched?.startDateTime || '').trim(), endDateTime: (watched?.endDateTime || '').trim(),
    conferenceId: (watched?.conferenceId || '').trim(),
    channelId: (watched?.channelId || '').trim(),
    holdCrmSyncUntilEnd: watched?.holdCrmSyncUntilEnd ?? false,
  }

  const isChanged = JSON.stringify(initial) !== JSON.stringify(current)
  const currentModeTypeDesc = resolveDescription(current.modeType, modeTypeOptions)
  const isOnDemand = isOnDemandMode(currentModeTypeDesc)
  const showDateTimeFields = current.modeType.length > 0 && !isOnDemand

  // Nothing is pulled from BigMarker when the page opens any more (ORB-942), so there is no
  // in-progress state: the badge only says whether the web is linked to a working integration.
  const syncBadge = integrationError ? (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${INTEGRATION_STATUS_COLORS.Error}`}>
      BigMarker Connection Error
    </span>
  ) : (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${BRAND_CLASSES.STATUS.SUCCESS}`}>
      Linked to BigMarker
    </span>
  )

  const requiredFields = [
    ...(isOnDemand || !current.modeType
      ? [current.eventType, current.modeType, current.title]
      : [current.eventType, current.modeType, current.title, current.timeZone, current.startDateTime, current.endDateTime]),
    // BigMarker is optional for an On-demand recording: no channel means no webinar (ORB-1076).
    ...(conferenceExists || isOnDemand ? [] : [current.channelId]),
  ]
  const requiredAnyEmpty = requiredFields.some(field => field.trim().length === 0)
  const dateOrderInvalid = showDateTimeFields && !!current.startDateTime && !!current.endDateTime && Date.parse(current.startDateTime) >= Date.parse(current.endDateTime)

  const saveDetails = async () => {
    onSaving(true)
    if (requiredAnyEmpty) {
      const channelMissing = !conferenceExists && !isOnDemand && current.channelId.trim().length === 0
      showError('Validation error', channelMissing
        ? 'Choose a BigMarker channel — it decides where the webinar is created at sign-off.'
        : showDateTimeFields ? 'All required fields including timezone, start and end times must be provided.' : 'Event Type, Mode, and Title are required.')
      onSaving(false)
      return
    }
    if (dateOrderInvalid) {
      showError('Validation error', 'Start date/time must be earlier than end date/time.')
      onSaving(false)
      return
    }
    const dto: OnlineEventDetailsPatchDto = {
      ETag: web.ETag, EventType: current.eventType, ModeType: current.modeType, Title: current.title,
      TimeZone: showDateTimeFields ? current.timeZone : undefined,
      StartDateTime: showDateTimeFields ? current.startDateTime : undefined,
      EndDateTime: showDateTimeFields ? current.endDateTime : undefined,
      ConferenceId: current.conferenceId || undefined,
      // Sent even when empty, which clears it: that is how an On-demand recording drops BigMarker.
      ChannelId: conferenceExists ? undefined : current.channelId,
      Overview: evt?.Overview || undefined,
      HoldCrmSyncUntilEnd: showDateTimeFields ? current.holdCrmSyncUntilEnd : undefined,
    }
    const res = await websApi.updateOnlineEventDetails(web.WorkspaceId, web.Id, dto)
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Event details updated', 'Your changes have been saved.')
      onSaved()
    } else if (res.status === 422) {
      const cascade = parseCascadeError(res)
      if (cascade) {
        setCascadeError(cascade)
        setPendingPatchDto(dto)
      }
      // Non-cascade 422s: api/client.ts already surfaced the validation error
      // via the global validationErrorHandler toast. Don't double-toast here.
    } else {
      showError('Save failed', res.error || 'Unable to update event details')
    }
    onSaving(false)
  }

  const retryPendingPatch = async () => {
    if (!pendingPatchDto) return
    onSaving(true)
    const res = await websApi.updateOnlineEventDetails(web.WorkspaceId, web.Id, pendingPatchDto)
    if (!res.error && res.data) {
      onPatchWeb(prev => ({ ...prev, ...res.data }))
      showSuccess('Event details updated', 'Your changes have been saved.')
      onSaved()
      setCascadeError(null)
      setPendingPatchDto(null)
    } else if (res.status !== 422) {
      showError('Save failed', res.error || 'Unable to update event details')
    }
    onSaving(false)
  }

  const handleCascadeDelete = async () => {
    if (!cascadeError) return
    for (const blocked of cascadeError.blockedBy) {
      const del = await touchPointsApi.deleteTouchPoint(web.WorkspaceId, blocked.touchPointId)
      if (del.error) {
        showError('Delete failed', `Could not delete touchpoint "${blocked.name}": ${del.error}`)
        return
      }
    }
    await retryPendingPatch()
  }

  const handleCascadeConvert = async () => {
    // TODO: convert-to-fixed-dates orchestration (fetch each TP, compute SendAtAbsolute
    // from anchor + offset, PATCH rule with Kind=Scheduled, retry Web patch).
    // Tracked in a follow-up ticket — the modal surfaces a "coming soon" note
    // locally until the orchestration lands.
  }

  const handleCascadeCancel = () => {
    setCascadeError(null)
    setPendingPatchDto(null)
  }

  const cancelDetailsEdit = () => {
    onCancel()
    formSetValue('eventType', resolveOptionKey(evt?.EventType, eventTypeOptions))
    formSetValue('modeType', resolveOptionKey(evt?.ModeType, modeTypeOptions))
    formSetValue('title', evt?.Title || '')
    formSetValue('timeZone', evt?.TimeZone || '')
    formSetValue('startDateTime', sanitizeDateTimeInput(evt?.StartDateTime))
    formSetValue('endDateTime', sanitizeDateTimeInput(evt?.EndDateTime))
    formSetValue('conferenceId', evt?.ConferenceId || '')
    // Otherwise a channel picked before Cancel rides along with the next save, which on an On-demand
    // recording quietly opts it into BigMarker (ORB-1076).
    formSetValue('channelId', evt?.ChannelId || seededChannelRef.current)
    formSetValue('holdCrmSyncUntilEnd', evt?.HoldCrmSyncUntilEnd ?? false)
  }

  type FormField = keyof OnlineEventAIForm
  const isDirtyEmpty = (field: FormField) =>
    formGetFieldState(field, currentFormState).isDirty && ((watched?.[field as keyof typeof watched] || '').toString().trim().length === 0)

  const timezoneDisplayName = evt?.TimeZone ? timeZones.find(tz => tz.Id === evt.TimeZone)?.DisplayName || evt.TimeZone : '\u2014'

  const eventTypeSelectOptions: SelectOption[] = eventTypeOptions.map(o => ({ value: o.Key, label: o.Description }))
  const modeTypeSelectOptions: SelectOption[] = modeTypeOptions.map(o => ({ value: o.Key, label: o.Description }))

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-primary-600" /> Event details</h2>
        {!editing ? (
          canEdit && <EditButton onClick={onEdit} disabled={editDisabled} />
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" onClick={saveDetails} disabled={saving || requiredAnyEmpty || !isChanged} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={cancelDetailsEdit} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
          </div>
        )}
      </div>
      {!editing ? (
        <div className="mt-4 space-y-5 text-sm">
          <DetailRow label="Title" value={evt?.Title || '\u2014'} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailRow label="Event Type" value={resolveDescription(evt?.EventType, eventTypeOptions)} />
            <DetailRow label="Mode" value={resolveDescription(evt?.ModeType, modeTypeOptions)} />
          </div>
          {!isOnDemandMode(evt?.ModeType) && (
            <>
              <DetailRow label="Timezone" value={timezoneDisplayName} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailRow label="Start" value={formatEventDateTime(evt?.StartDateTime)} />
                <DetailRow label="End" value={formatEventDateTime(evt?.EndDateTime)} />
              </div>
            </>
          )}
          {evt?.ConferenceNotFound && (
            <div className={`flex items-start gap-2 p-3 rounded-lg ${BRAND_CLASSES.STATUS.SYNC_ERROR}`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Conference (ID: {evt.ConferenceId}) not found in {integrationName}.
                It may have been deleted externally. Please verify the conference exists or update the Conference ID.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailRow label="Conference Id" value={
              evt?.ConferenceId
                ? <span className="inline-flex items-center gap-2">
                    {evt.ConferenceId}
                    {syncBadge}
                  </span>
                : '\u2014'
            } />
            <DetailRow label="BigMarker Channel" value={
              evt?.ChannelId
                ? (channels.find(c => c.ChannelId === evt.ChannelId)?.Name || evt.ChannelId)
                : '\u2014'
            } />
          </div>
          <DetailRow label="Conference Join URL" value={evt?.ConferenceJoinUrl ? (
            <a href={evt.ConferenceJoinUrl} target="_blank" className="inline-flex items-center gap-1 text-blue-600 hover:underline"><Globe className="w-4 h-4" /> {evt.ConferenceJoinUrl}</a>
          ) : '\u2014'} />
          {showDateTimeFields && (
            <DetailRow label="Hold CRM Sync Until End" value={
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${evt?.HoldCrmSyncUntilEnd ? BRAND_CLASSES.STATUS.SUCCESS : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                {evt?.HoldCrmSyncUntilEnd ? 'Enabled' : 'Disabled'}
              </span>
            } />
          )}
        </div>
      ) : (
        <form className="mt-4 space-y-6" onSubmit={(e) => e.preventDefault()}>
          <TextBoxField control={control} name="title" labelSize="xs" label="Title" showCharCounter maxLength={200} error={isDirtyEmpty('title') ? 'Required' : undefined} placeholder="Event title" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select labelSize="xs" label="Event Type" name="eventType" options={eventTypeSelectOptions} value={formWatch('eventType')} onChange={(e: Event) => formSetValue('eventType', (e.target as HTMLSelectElement).value, { shouldDirty: true })} error={isDirtyEmpty('eventType') ? 'Required' : undefined} placeholder="Select event type" />
            <Select labelSize="xs" label="Mode" name="modeType" options={modeTypeSelectOptions} value={formWatch('modeType')} onChange={(e: Event) => formSetValue('modeType', (e.target as HTMLSelectElement).value, { shouldDirty: true })} error={isDirtyEmpty('modeType') ? 'Required' : undefined} placeholder="Select mode" />
          </div>
          {showDateTimeFields && (
            <>
              <TimeZoneCombobox labelSize="xs" label="Timezone" name="timeZone" required value={formWatch('timeZone')} onChange={(tz) => formSetValue('timeZone', tz, { shouldDirty: true })} error={isDirtyEmpty('timeZone') ? 'Required' : undefined} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DateTimePicker labelSize="xs" label="Start" required error={isDirtyEmpty('startDateTime') ? 'Required' : undefined} {...formRegister('startDateTime')} />
                <DateTimePicker labelSize="xs" label="End" required error={(formGetFieldState('endDateTime', currentFormState).isDirty && ((watched?.endDateTime || '').trim().length === 0 || dateOrderInvalid)) ? (dateOrderInvalid && (watched?.endDateTime || '').trim().length > 0 ? 'End must be after start' : 'Required') : undefined} {...formRegister('endDateTime')} />
              </div>
            </>
          )}
          {!conferenceExists && (
            <Select
              labelSize="xs"
              label="BigMarker Channel"
              name="channelId"
              required={!isOnDemand}
              options={[
                // A selectable "none", because the placeholder can't be chosen again once a channel has been picked.
                ...(isOnDemand ? [{ value: '', label: NO_CHANNEL_LABEL }] : []),
                ...channels.map(c => ({ value: c.ChannelId, label: c.Name || c.ChannelId })),
              ]}
              value={formWatch('channelId')}
              onChange={(e: Event) => formSetValue('channelId', (e.target as HTMLSelectElement).value, { shouldDirty: true })}
              error={!isOnDemand && isDirtyEmpty('channelId') ? 'Required' : undefined}
              placeholder={isOnDemand ? NO_CHANNEL_LABEL : channels.length === 0 ? 'No BigMarker channels available' : 'Select a channel'}
              helperText={isOnDemand
                ? 'Optional. Leave it on None to embed the recording on the thank-you page, or choose a channel to host it in BigMarker.'
                : 'Where the webinar is created when this web is signed off. Fixed afterwards.'}
            />
          )}
          <TextBoxField control={control} name="conferenceId" labelSize="xs" label="Conference Id" disabled={(evt?.ConferenceId || '').trim().length > 0 && !evt?.ConferenceNotFound} maxLength={100} placeholder="Optional" helperText={evt?.ConferenceNotFound ? 'Enter the matching conference ID.' : undefined} />
          <TextBox labelSize="xs" label="Conference Join URL" name="conferenceJoinUrl" type="url" disabled value={evt?.ConferenceJoinUrl || ''} onChange={() => {}} helperText="Set by BigMarker; not editable here." />
          {showDateTimeFields && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <ToggleRow
                label="Hold CRM sync until event ends"
                checked={current.holdCrmSyncUntilEnd}
                onChange={(v) => formSetValue('holdCrmSyncUntilEnd', v, { shouldDirty: true })}
                disabled={saving}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                When enabled, registrations are captured immediately but CRM/HubSpot sync is deferred until after the live event ends. Attendance status (attended / did not attend) is included in the deferred sync.
              </p>
            </div>
          )}
        </form>
      )}

      <WebTypeCascadeModal
        isOpen={cascadeError !== null}
        error={cascadeError}
        onConvert={handleCascadeConvert}
        onDelete={handleCascadeDelete}
        onCancel={handleCascadeCancel}
      />
    </div>
  )
}
